import { Recipe } from '../types/recipe';
import { stripInstructionHtml } from './stripInstructionHtml';
import { normalizeInstructions, coerceInstructionSections } from './normalizeInstructions';
import { parseDuration } from './parseDuration';
import { stripStepNumbers } from './stepNumbering';

/**
 * Single source of truth for deriving cooking-mode steps from a recipe.
 *
 * Handles both storage shapes:
 *  - `instructionSections` (already sectioned, e.g. enriched recipes)
 *  - flat `instructions[]` (scraped recipes with fragments/headers mixed in),
 *    repaired through normalizeInstructions.
 *
 * Every cooking surface (PlanningView timeline, CookingView, CookingModeModal)
 * MUST consume steps through this util — never iterate recipe.instructions
 * directly, or step counts drift between screens.
 */
export interface CookingStep {
  /** Cleaned display text (HTML + the publisher's step numbers stripped). */
  text: string;
  /** Section header this step belongs to, if the recipe has named sections. */
  sectionName: string | null;
  /** 1-based continuous number across all sections. */
  globalNumber: number;
  /** Timer duration parsed from the step text ("about 5 minutes" → 300), or null. */
  durationSec: number | null;
}

export interface CookingStepsResult {
  steps: CookingStep[];
  hasSections: boolean;
}

// Numbering is not stripped here: whether a bare "2 " is a step number depends
// on the whole list (stepNumbering.ts). normalizeInstructions strips the flat
// list; the sectioned branch below strips across its sections.
function cleanStepText(raw: string): string {
  return stripInstructionHtml(raw).trim();
}

export function getCookingSteps(recipe: Recipe): CookingStepsResult {
  const entries: { text: string; sectionName: string | null }[] = [];
  let hasSections = false;

  // Coerced because some recipes persist `steps` as plain strings (#595) —
  // reading `.step` off those yields undefined and cooking mode lists nothing.
  // An empty result means no section carried a usable step, so fall through to
  // the flat list rather than showing a stepless recipe.
  const sections = coerceInstructionSections(recipe.instructionSections);

  if (sections.length > 0) {
    hasSections = sections.some(s => !!s.name);
    for (const section of sections) {
      for (const st of section.steps) {
        entries.push({
          text: cleanStepText(st.step),
          sectionName: hasSections ? (section.name ?? null) : null,
        });
      }
    }
    const { texts } = stripStepNumbers(entries.map(e => e.text));
    texts.forEach((text, i) => { entries[i].text = text.trim(); });
  } else {
    const preStripped = (recipe.instructions ?? []).map((i, idx) => {
      if (typeof (i as unknown) === 'string') {
        return { number: idx + 1, step: cleanStepText(i as unknown as string) };
      }
      return { ...i, step: cleanStepText(i.step) };
    });
    const normalized = normalizeInstructions(preStripped);
    hasSections = normalized.hasSections;
    for (const section of normalized.sections) {
      for (const st of section.steps) {
        entries.push({
          text: st.step,
          sectionName: hasSections ? (section.name ?? null) : null,
        });
      }
    }
  }

  const steps: CookingStep[] = entries
    .filter(e => e.text.length > 0)
    .map((e, i) => ({
      text: e.text,
      sectionName: e.sectionName,
      globalNumber: i + 1,
      durationSec: parseDuration(e.text),
    }));

  return { steps, hasSections };
}
